/**
 * Материал режима: токены и фрагменты, в которых они живут.
 *
 * Состав фрагмента не перечисляется рядом с ним, а вычисляется по самому
 * тексту тем же поиском, что разбирает результат прогона: список,
 * записанный руками, разошёлся бы с текстом при первой же правке — и
 * упражнение предложило бы токен, которого пользователю ещё не показывали.
 */

import { findTokenSpans } from '@type2x/typing-engine';

import type { PackFragment, PackMode } from '../contracts/packs.js';
import { DEVELOPER_GROUPS, DEVELOPER_TEXTS } from '../material/developer.js';
import { EMAIL_WEB_GROUPS, EMAIL_WEB_TEXTS } from '../material/email-web.js';

/**
 * Символы, которые фрагменту разрешены помимо токенов пакета.
 *
 * Буквы и цифры — это то, вокруг чего стоит токен, и тренирует их другой
 * режим. Всё остальное во фрагменте оказалось бы клавишей, которую
 * пользователю не показывали; проверяется тестом.
 */
export const ALLOWED_EXTRAS = 'abcdefghijklmnopqrstuvwxyz0123456789 ';

/** Группы токенов режима в порядке открытия. */
export function packTokenGroups(mode: PackMode): readonly (readonly string[])[] {
  return mode === 'developer-pack' ? DEVELOPER_GROUPS : EMAIL_WEB_GROUPS;
}

/** Все токены режима в порядке открытия. */
export function packTokens(mode: PackMode): string[] {
  return packTokenGroups(mode).flat();
}

/** Тексты фрагментов режима. */
function packTexts(mode: PackMode): readonly string[] {
  return mode === 'developer-pack' ? DEVELOPER_TEXTS : EMAIL_WEB_TEXTS;
}

/** Фрагменты режима вместе с токенами, которые в них встречаются. */
export function packFragments(mode: PackMode): PackFragment[] {
  const tokens = packTokens(mode);

  return packTexts(mode).map((text) => ({
    text,
    tokens: [...new Set(findTokenSpans(text, tokens).map((span) => span.token))],
  }));
}

/**
 * Фрагменты, посильные при открытых токенах.
 *
 * Фрагмент с ещё не открытым токеном потребовал бы сочетания, которого
 * пользователю не показывали, — таких в упражнении быть не должно.
 */
export function affordableFragments(mode: PackMode, unlocked: readonly string[]): PackFragment[] {
  const open = new Set(unlocked);
  return packFragments(mode).filter((fragment) => fragment.tokens.every((t) => open.has(t)));
}
